import {
  Box,
  Card,
  CircularProgress,
  CircularProgressLabel,
  Grid,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useMediaQuery,
} from "@chakra-ui/react";

import React, { useState } from "react";
import { Circle } from "@chakra-ui/react";
import { InfoOutlineIcon } from "@chakra-ui/icons";
import { customCurrencyFormat, getPixelCount } from "../../../utils";

const AdvancedMetricCircle = ({
  title = "",
  label = "",
  profit = "-",
  profitPercentage = "-",
  data = {},
  perfectPL = "-",
  color = "#458CFF",
  status = false,
  showPL = true,
  dimensions = null,
  circleDimensions = null,
  pdfFormat = false,
  invert = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const toggle = () => {
    setIsModalOpen(!isModalOpen);
  };
  const openModal = () => {
    setIsModalOpen(true);
  };

  return (
    <Card
      fontFamily={"lato"}
      width={dimensions ? dimensions : "248px"}
      height={dimensions ? dimensions : "248px"}
      borderRadius="full"
      display="flex"
      alignItems="center"
      justifyContent="center"
      boxShadow={!pdfFormat ? "xl" : "none"}
    >
      {status && (
        <StatusModal
          isOpen={isModalOpen}
          toggle={toggle}
          color={color}
          data={data}
          invert={invert}
        />
      )}
      <CircularProgress
        value={100}
        size={circleDimensions ? circleDimensions : "268px"}
        thickness="6px"
        color={color}
      >
        <CircularProgressLabel>
          <Box textAlign="center">
            <Box
              fontSize={pdfFormat ? "14px" : "20px"}
              fontWeight="600"
              display="flex"
              alignItems="center"
              justifyContent="center"
              color="#6F7D8C"
              height={"24px"}
            >
              {title}
              {status && (
                <Circle
                  cursor="pointer"
                  fontSize="large"
                  color="#6F7D8C"
                  size="16px"
                  bg="white"
                  marginLeft={1}
                  onClick={openModal}
                >
                  <InfoOutlineIcon size={"16px"} />
                </Circle>
              )}
            </Box>
          </Box>
          <Box>
            <Text
              fontSize={getPixelCount(
                pdfFormat ? "37px" : "40px",
                profit,
                8,
                3
              )}
              fontWeight="700"
              color={color}
              height={"55px"}
            >
              {profit}
            </Text>
            <Text fontSize={pdfFormat ? "18px" : "22px"} fontWeight="700">
              {profitPercentage}
            </Text>
            {showPL && (
              <Text
                fontSize={pdfFormat ? "12px" : "15px"}
                lineHeight={"18px"}
                fontWeight="600"
                color={"#6F7D8C"}
              >
                {label} {perfectPL}
              </Text>
            )}
          </Box>
        </CircularProgressLabel>
      </CircularProgress>
    </Card>
  );
};

export default AdvancedMetricCircle;

const StatusModal = ({ isOpen, toggle, color, data, invert = false }) => {
  const [isMobile] = useMediaQuery("(max-width: 767px)");

  const statusItems = [
    {
      label: "Positive",
      value: data?.positive_value,
      percent: customCurrencyFormat(data?.positive_percent, true),
      color: "#68BA65",
      symbol: invert ? "-" : "+",
    },
    {
      label: "Neutral",
      value: data?.neutral_value,
      percent: `${customCurrencyFormat(
        data?.positive_percent,
        true
      )} - ${customCurrencyFormat(data?.warning_percent, true)}`,
      color: "#458CFF",
      symbol: "",
    },
    {
      label: "Warning",
      value: data?.warning_value,
      percent: `${customCurrencyFormat(
        data?.warning_percent,
        true
      )} - ${customCurrencyFormat(data?.caution_percent, true)}`,
      color: "#F1B20E",
      symbol: "",
    },
    {
      label: "Caution",
      value: data?.caution_value,
      percent: customCurrencyFormat(data?.caution_percent, true),
      color: "#FF5E5E",
      symbol: invert ? "+" : "-",
    },
  ];

  const StatusCard = ({ label, value, percent, statusColor, symbol }) => {
    const isSelected = statusColor === color;

    return (
      <Box
        textAlign="center"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <Card
          boxShadow="xl"
          m={6}
          mb={3}
          w={isSelected ? "210px" : "200px"}
          h={isSelected ? "210px" : "200px"}
          borderRadius="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          opacity={isSelected ? "100%" : "70%"}
          borderWidth={isSelected ? "3px" : "0px"}
          borderColor={isSelected ? "#536DEC" : "black"}
        >
          <CircularProgress
            value={100}
            size={isSelected ? "220px" : "210px"}
            thickness="6px"
            color={statusColor}
          >
            <CircularProgressLabel>
              <Box textAlign="center">
                <Text fontSize="17px" fontWeight="bold" mb={1}>
                  {label}
                </Text>
                <Text
                  fontSize={getPixelCount(
                    "35px",
                    customCurrencyFormat(value),
                    8,
                    3
                  )}
                  fontWeight="bold"
                  mb={1}
                  color={statusColor}
                >
                  {customCurrencyFormat(value)}
                </Text>
                <Text fontSize="14px" fontWeight="bold" mb={1}>
                  {value !== "-"
                    ? `${percent}
                 ${symbol}`
                    : "-"}
                </Text>
              </Box>
            </CircularProgressLabel>
          </CircularProgress>
        </Card>
        {isSelected && (
          <Text color="#536DEC" fontSize="large" fontWeight="extrabold">
            Your Current Status
          </Text>
        )}
      </Box>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={toggle} isCentered>
      <ModalOverlay />
      <ModalContent
        height={!isMobile ? "auto" : "95%"}
        fontFamily="lato"
        maxW={1000}
        width="95%"
        p="20px 0"
        borderRadius="12px"
      >
        <ModalHeader
          textAlign="center"
          fontSize="24px"
          fontWeight="bold"
          pt="8px"
          pb="2px"
        >
          <Box display="flex" flexDirection="column" alignItems="center">
            <Text>Current Status</Text>
            <Box border="1px" borderColor="#536DEC" width="95px" mt="4px" />
          </Box>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody
          display="flex"
          flex="2 2"
          flexWrap="wrap"
          justifyContent="center"
          overflowY="auto"
          p={0}
          h="100%"
          overflowX="hidden"
          mr={isMobile ? 4 : 0}
          ml={isMobile ? 7 : 0}
          sx={{
            "&::-webkit-scrollbar": {
              width: "9px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#30384A",
              borderRadius: "7px",
            },
            "&::-webkit-scrollbar-track": {
              background: "gray.100",
              borderRadius: "4px",
            },
          }}
        >
          <Grid
            templateColumns={{
              base: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(4, 1fr)",
            }}
            alignItems="center"
            justifyItems="center"
          >
            {statusItems.map(({ label, value, percent, color, symbol }) => (
              <StatusCard
                key={label}
                label={label}
                value={value}
                percent={percent}
                statusColor={color}
                symbol={symbol}
              />
            ))}
          </Grid>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
